import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { marketingTranslations } from '../i18n/marketingTranslations';

const STORAGE_KEY = 'allure_lang';
const DEFAULT_LANG = 'en';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'el', label: 'Ελληνικά', short: 'EL' },
];

const LanguageContext = createContext({
  language: DEFAULT_LANG,
  setLanguage: () => {},
  t: (key) => key,
});

function readStoredLanguage() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.some((l) => l.code === stored)) return stored;
  } catch (_e) { /* noop */ }
  return DEFAULT_LANG;
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => readStoredLanguage());

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, language); } catch (_e) { /* noop */ }
    try { document.documentElement.setAttribute('lang', language); } catch (_e) { /* noop */ }
  }, [language]);

  const setLanguage = useCallback((code) => {
    if (!SUPPORTED_LANGUAGES.some((l) => l.code === code)) return;
    setLanguageState(code);
  }, []);

  const t = useCallback(
    (key) => {
      const localized = marketingTranslations[language]?.[key];
      if (localized !== undefined) return localized;
      const fallback = marketingTranslations[DEFAULT_LANG]?.[key];
      return fallback !== undefined ? fallback : key;
    },
    [language],
  );

  const value = useMemo(() => ({ language, setLanguage, t }), [language, setLanguage, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}
